/* ------------------------------------------------------------------
 * Mojito Cart Upsell — "People Also Bought" controller
 * ------------------------------------------------------------------
 * Loaded site-wide from snippets/cart-drawer.liquid (outside the cloned
 * <template>, so it actually runs). It:
 *
 *   1. Finds the host [data-mojito-cart-upsell] inside the cart drawer.
 *   2. Reads /cart.js to pick the ANCHOR (highest-value line item) and to
 *      fingerprint the cart.
 *   3. Fetches sections/mojito-cart-upsell.liquid via the recommendations
 *      endpoint (intent=complementary) for the anchor, with a /cart fallback,
 *      and swaps the populated cards into the host.
 *   4. Re-runs whenever the cart changes (the theme re-renders the drawer
 *      subtree on every add/remove/qty change) — de-duped via the fingerprint
 *      so it never loops on its own swaps.
 *
 * No dependency on theme.js internals; the "Add" buttons inside the injected
 * cards are the theme's own <product-form>/<quick-add-button> custom elements,
 * which self-upgrade on insertion.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var SECTION = 'mojito-cart-upsell';
  var HOST_SELECTOR = '[data-mojito-cart-upsell]';
  var SUPPRESS_MS = 600; // ignore mutations caused by our own innerHTML swap

  var lastFingerprint = null;
  var inFlight = false;
  var suppressUntil = 0;
  var debounceTimer = null;

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    return {
      recs: r.product_recommendations_url || '/recommendations/products',
      cart: r.cart_url || '/cart'
    };
  }

  function hosts() {
    return Array.prototype.slice.call(document.querySelectorAll(HOST_SELECTOR));
  }

  function fingerprint(cart) {
    if (!cart || !cart.items) return '0';
    var sig = cart.items
      .map(function (i) { return i.id + 'x' + i.quantity; })
      .join(',');
    return cart.item_count + '|' + sig;
  }

  function anchorId(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;
    var best = cart.items[0];
    for (var i = 1; i < cart.items.length; i++) {
      if ((cart.items[i].final_line_price || 0) > (best.final_line_price || 0)) {
        best = cart.items[i];
      }
    }
    return best.product_id;
  }

  function fetchText(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      return r.ok ? r.text() : Promise.reject(r.status);
    });
  }

  function extractContent(htmlText) {
    var doc = new DOMParser().parseFromString(htmlText, 'text/html');
    var node = doc.querySelector('[data-cart-upsell-content]');
    return node ? node.innerHTML : '';
  }

  function swapAll(html) {
    suppressUntil = Date.now() + SUPPRESS_MS;
    hosts().forEach(function (host) {
      var slot = host.querySelector('[data-cart-upsell-content]');
      if (!slot) {
        slot = document.createElement('div');
        slot.setAttribute('data-cart-upsell-content', '');
        host.appendChild(slot);
      }
      slot.innerHTML = html;
      var hasCards = !!slot.querySelector('[data-cart-upsell-card]');
      host.classList.toggle('is-empty', !hasCards);
      if (hasCards) bindArrows(host);
    });
  }

  function bindArrows(host) {
    var scroller = host.querySelector('[data-pab-scroller]');
    var nav = host.querySelector('[data-pab-nav]');
    var prev = host.querySelector('[data-pab-prev]');
    var next = host.querySelector('[data-pab-next]');
    if (!scroller) return;

    function step() {
      var card = scroller.querySelector('.mojito-pab-card');
      return card ? card.offsetWidth + 10 : 200;
    }
    function update() {
      var canScroll = scroller.scrollWidth - scroller.clientWidth > 1;
      if (nav) {
        nav.style.display = canScroll ? '' : 'none';
        nav.setAttribute('aria-hidden', canScroll ? 'false' : 'true');
      }
      if (prev) prev.disabled = scroller.scrollLeft <= 0;
      if (next) next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
    }

    // Elements are recreated on every swap, so listeners are fresh each time.
    if (prev) prev.addEventListener('click', function () {
      scroller.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      scroller.scrollBy({ left: step(), behavior: 'smooth' });
    });
    scroller.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(update);
    setTimeout(update, 150);
  }

  function refresh(force) {
    if (inFlight) return;
    if (!hosts().length) return;
    inFlight = true;
    var release = function () { inFlight = false; };

    fetchText(routes().cart + '.js')
      .then(function (txt) { return JSON.parse(txt); })
      .then(function (cart) {
        var fp = fingerprint(cart);
        if (!force && fp === lastFingerprint) { release(); return; }
        lastFingerprint = fp;

        if (!cart.item_count) { swapAll(''); release(); return; }

        var anchor = anchorId(cart);
        var recUrl =
          routes().recs +
          '?section_id=' + encodeURIComponent(SECTION) +
          '&product_id=' + encodeURIComponent(anchor) +
          '&intent=complementary&limit=10';
        var fallbackUrl = routes().cart + '?section_id=' + encodeURIComponent(SECTION);

        return fetchText(recUrl)
          .then(function (html) {
            var content = extractContent(html);
            // If the recs endpoint gave us cards, use them; otherwise fall back
            // to the cart-context render (manual + top-rated only).
            if (content && content.indexOf('data-cart-upsell-card') !== -1) return content;
            return fetchText(fallbackUrl).then(extractContent);
          })
          .catch(function () {
            return fetchText(fallbackUrl).then(extractContent).catch(function () { return ''; });
          })
          .then(function (content) { swapAll(content || ''); release(); });
      })
      .catch(release);
  }

  function scheduleRefresh() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { refresh(false); }, 300);
  }

  /* ----------------------------------------------------------------
   * Order attribution — tag added items with a hidden line-item
   * property `_rec_source` so the merchant can trace which orders came
   * from this widget (same mechanism as sections/mojito-recommended-top).
   *
   * Single-variant cards already carry properties[_rec_source] directly in
   * their quick-add form. This covers the OTHER path: clicking a card's
   * image / title / "View" navigates to the product page, where we inject
   * the same property into the main add-to-cart form on arrival.
   *
   * We deliberately reuse the top bar's STORAGE_PREFIX + `_rec_source` key
   * so every recommendation source funnels through ONE property (this widget
   * just uses a distinct value), and the duplicate-input + submit-bound
   * guards keep both receivers idempotent if they run on the same page.
   * ---------------------------------------------------------------- */
  var REC_SOURCE = 'cart-people-also-bought';
  var STORAGE_PREFIX = 'mojito_rec_click_';
  var MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

  function captureClick(e) {
    var nav = e.target.closest('.mojito-pab-card a[href], .mojito-pab-card [data-button-quick-view]');
    if (!nav) return;
    var card = e.target.closest('.mojito-pab-card');
    if (!card) return;
    var handle = card.getAttribute('data-rec-product-handle');
    if (!handle) return;
    try {
      sessionStorage.setItem(STORAGE_PREFIX + handle, JSON.stringify({ source: REC_SOURCE, ts: Date.now() }));
    } catch (err) { /* private mode / quota — silent */ }
  }

  function injectAttribution() {
    var match = window.location.pathname.match(/\/products\/([^\/?#]+)/);
    if (!match) return;
    var handle = match[1];
    var key = STORAGE_PREFIX + handle;

    var raw;
    try { raw = sessionStorage.getItem(key); } catch (e) { return; }
    if (!raw) return;

    var data;
    try { data = JSON.parse(raw); } catch (e) { return; }
    if (!data || !data.source) return;

    if (Date.now() - (data.ts || 0) > MAX_AGE_MS) {
      try { sessionStorage.removeItem(key); } catch (e) {}
      return;
    }

    var forms = document.querySelectorAll('form[action*="/cart/add"][data-product-form]:not([data-quickbuy-form])');
    forms.forEach(function (form) {
      if (form.querySelector('input[name="properties[_rec_source]"]')) return;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_rec_source]';
      input.value = data.source;
      form.appendChild(input);

      if (!form.dataset.mojitoRecSubmitBound) {
        form.dataset.mojitoRecSubmitBound = '1';
        form.addEventListener('submit', function () {
          try { sessionStorage.removeItem(key); } catch (e) {}
        });
      }
    });
  }

  function initAttribution() {
    // Capture in the capture phase so it lands before the link navigates.
    document.addEventListener('click', captureClick, true);
    injectAttribution();
    // Re-inject after Palo Alto re-renders the product form on variant change.
    document.addEventListener('theme:product-info:loaded', injectAttribution);
  }

  function init() {
    // Cart page may already have the host present.
    refresh(true);
    initAttribution();

    var drawer = document.querySelector('[data-cart-drawer]') ||
                 document.getElementById('cart-drawer');

    // The drawer is cloned from a <template> on first open and its subtree is
    // re-rendered on every cart change. Watch it to (re)populate the upsell.
    var watched = drawer || document.body;
    var observer = new MutationObserver(function () {
      if (Date.now() < suppressUntil) return;
      scheduleRefresh();
    });
    observer.observe(watched, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
